import { Injectable } from '@angular/core';
import { db } from '@database/db';
import { photoDB, PhotoBlobRecord } from '@database/photo-db';
import { Photo } from '@entities/photo.class';
import { enregistrerErreurTelemetrie } from '@services/core/telemetrie';

@Injectable({
	providedIn: 'root'
})
export class PhotoBlobService {
	
	/** Cache d'objectUrls partagé entre tous les composants (survit aux destroy/recreate) */
	private objectUrlCache = new Map<string, string>();

	/**
	 * Retourne un objectUrl depuis le cache s'il existe, sinon charge depuis IndexedDB,
	 * crée l'objectUrl, le met en cache et le retourne.
	 */
	async getObjectUrl(photoId: string): Promise<string | undefined> {
		const cached = this.objectUrlCache.get(photoId);
		if(cached) {
			return cached;
		}

		const blob = await this.getBlob(photoId);
		if(!blob) {
			return undefined;
		}

		const objectUrl = URL.createObjectURL(blob);
		this.objectUrlCache.set(photoId, objectUrl);
		return objectUrl;
	}

	/**
	 * Enregistre dans le cache une objectUrl créée ailleurs (photo fraîchement importée).
	 *
	 * Le cache est la définition même d'une URL VIVANTE : prepareRuntimePhotos et les
	 * galeries jugent périmée toute URL blob: qui n'y figure pas, et la remplacent —
	 * ce qui, le temps d'un cycle de rendu, laisse un <img src=""> dont l'événement
	 * `error` déclenchait la purge du blob tout juste stocké. Une URL créée par
	 * URL.createObjectURL sans passer par ici était donc condamnée.
	 */
	registerObjectUrl(photoId: string, objectUrl: string): void {
		const precedente = this.objectUrlCache.get(photoId);
		if (precedente && precedente !== objectUrl) {
			URL.revokeObjectURL(precedente);
		}
		this.objectUrlCache.set(photoId, objectUrl);
	}

	/**
	 * Libère les objectUrls des photos qui ne sont PAS dans `aGarder` — appelé à
	 * l'ouverture d'un dossier avec les photos de ce dossier. Le cache ne se vidait
	 * jamais : au fil de la journée, les photos de tous les dossiers ouverts restaient
	 * retenues (mémoire des iPad, « page blanche » intermittente, 02/10). Les photos du
	 * dossier ouvert ne sont jamais touchées ; celles d'un autre dossier seront rechargées
	 * depuis l'appareil (prepareRuntimePhotos) si on le rouvre.
	 */
	libererSauf(aGarder: Set<string>): number {
		let liberees = 0;
		for (const [photoId, url] of this.objectUrlCache) {
			if (aGarder.has(photoId)) continue;
			URL.revokeObjectURL(url);
			this.objectUrlCache.delete(photoId);
			liberees++;
		}
		return liberees;
	}

	/** Nombre d'objectUrls vivantes (télémétrie). */
	get tailleCache(): number {
		return this.objectUrlCache.size;
	}

	/**
	 * Invalide un objectUrl du cache (et le révoque)
	 */
	revokeObjectUrl(photoId: string): void {
		const url = this.objectUrlCache.get(photoId);
		if(url) {
			URL.revokeObjectURL(url);
			this.objectUrlCache.delete(photoId);
		}
	}

	/**
	 * Jette uniquement les octets en cache d'une photo (objectUrl + blob IndexedDB),
	 * sans toucher à ses métadonnées. Utilisé quand le navigateur n'a pas su décoder
	 * l'image et qu'il faut la redemander à l'API.
	 *
	 * Contrairement à deleteBlob, ne supprime PAS l'entrée db.photo : la photo existe
	 * toujours dans le rapport, seuls ses octets sont périmés.
	 */
	async discardCachedBlob(photoId: string): Promise<void> {
		this.revokeObjectUrl(photoId);
		try {
			await photoDB.photo_blobs.delete(photoId);
		} catch (error) {
			console.warn('⚠️ Impossible de purger le blob en cache:', photoId, error);
		}
	}

	/**
	 * Vérifie si un objectUrl est déjà en cache (synchrone)
	 */
	getCachedObjectUrl(photoId: string): string | undefined {
		return this.objectUrlCache.get(photoId);
	}
	/**
	 * Stocke un blob de photo dans IndexedDB (table photo_blobs)
	 * 🍎 Stockage en base64 pour compatibilité iOS (contourne le bug WebKit)
	 */
	async storeBlob(photoId: string, rapportId: number, blob: Blob): Promise<void> {
		const startTime = performance.now();
		try {
			// Convertir le blob en base64 pour le stockage
			const base64StartTime = performance.now();
			const base64 = await this.blobToBase64(blob);
			const base64EndTime = performance.now();
			
			const record: PhotoBlobRecord = {
				photoId,
				rapportId,
				base64,
				createdAt: new Date(),
				lastAccessedAt: new Date()
			};
			
			const dbStartTime = performance.now();
			await photoDB.photo_blobs.put(record);
			const dbEndTime = performance.now();
			
			const fullSizeKB = (blob.size / 1024).toFixed(2);
			const totalTime = (performance.now() - startTime).toFixed(2);
			console.log(`📦 Blob stocké pour photo: ${photoId} | Full: ${fullSizeKB} KB | Total: ${totalTime}ms (B64: ${(base64EndTime - base64StartTime).toFixed(2)}ms, DB: ${(dbEndTime - dbStartTime).toFixed(2)}ms)`);
		} catch (error) {
			console.error('❌ Erreur lors du stockage du blob:', error);
			throw error;
		}
	}
	
	/**
	 * Convertit un Blob en base64
	 */
	private blobToBase64(blob: Blob): Promise<string> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onloadend = () => {
				if (typeof reader.result === 'string') {
					resolve(reader.result);
				} else {
					reject(new Error('Conversion blob vers base64 échouée'));
				}
			};
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});
	}
	
	/**
	 * Convertit un base64 en Blob
	 * Utilise fetch() qui fait la conversion off main thread (pas de freeze UI)
	 */
	private async base64ToBlob(base64: string): Promise<Blob> {
		const response = await fetch(base64);
		return response.blob();
	}


	/**
	 * Récupère un blob depuis IndexedDB (converti depuis base64)
	 * 🍎 Conversion base64 → Blob pour utilisation avec objectUrl
	 */
	async hasBlob(photoId: string): Promise<boolean> {
		try {
			const count = await photoDB.photo_blobs.where('photoId').equals(photoId).count();
			return count > 0;
		} catch (error) {
			console.error('❌ Erreur lors de la vérification du blob:', error);
			return false;
		}
	}

	async getBlob(photoId: string): Promise<Blob | undefined> {
		try {
			const record = await photoDB.photo_blobs.get(photoId);
			
			if (record) {
				// Mettre à jour lastAccessedAt en fire-and-forget (non bloquant)
				// C'est juste du LRU pour le cleanup, pas besoin d'attendre le flush disque
				photoDB.photo_blobs.update(photoId, {
					lastAccessedAt: new Date()
				}).catch(() => { /* ignore */ });
				
				// Convertir base64 → Blob (async, off main thread via fetch)
				const blob = await this.base64ToBlob(record.base64);
				return blob;
			}
			
			return undefined;
		} catch (error) {
			console.error('❌ Erreur lors de la récupération du blob:', error);
			return undefined;
		}
	}

	/**
	 * Charge en masse les objectUrls pour une liste de photoIds.
	 * Utilise bulkGet (1 transaction) au lieu de N get() individuels.
	 * Retourne une Map<photoId, objectUrl>.
	 */
	async bulkGetObjectUrls(photoIds: string[]): Promise<Map<string, string>> {
		const result = new Map<string, string>();
		const idsToLoad: string[] = [];

		// 1. Récupérer ce qui est déjà en cache
		for (const id of photoIds) {
			const cached = this.objectUrlCache.get(id);
			if (cached) {
				result.set(id, cached);
			} else {
				idsToLoad.push(id);
			}
		}

		if (idsToLoad.length === 0) return result;

		// Tout ce bloc était un seul try/catch autour d'un bulkGet + des Promise.all :
		// UNE photo impossible à lire ou à convertir (record de 67 Mo — une photo de
		// 50 Mo importée sans compression —, transaction IndexedDB avortée par Safari…)
		// faisait échouer l'ensemble et TOUTES les photos du rapport ressortaient
		// blanches, y compris celles parfaitement saines (BI 30010, 148 photos, 20/09).
		// Chaque photo est désormais traitée pour elle-même.
		try {
			// 2. Lecture bulk depuis IndexedDB (1 seule transaction), repli unitaire si
			// la transaction groupée échoue.
			let records: (PhotoBlobRecord | undefined)[];
			try {
				records = await photoDB.photo_blobs.bulkGet(idsToLoad);
			} catch (bulkError) {
				console.warn('⚠️ bulkGet photo_blobs en échec, lecture unitaire:', bulkError);
				records = await Promise.all(
					idsToLoad.map(id => photoDB.photo_blobs.get(id).catch(() => undefined))
				);
			}

			// 3. Convertir base64 → Blob en parallèle par batch (évite flood de fetch)
			const CONVERT_BATCH = 20;
			const validRecords: { id: string; base64: string }[] = [];
			for (let i = 0; i < records.length; i++) {
				const rec = records[i];
				if (rec?.base64) {
					validRecords.push({ id: idsToLoad[i], base64: rec.base64 });
				}
			}

			const echecs: { id: string; taille: number; erreur: unknown }[] = [];
			for (let i = 0; i < validRecords.length; i += CONVERT_BATCH) {
				const batch = validRecords.slice(i, i + CONVERT_BATCH);
				const blobs = await Promise.allSettled(
					batch.map(r => this.base64ToBlob(r.base64))
				);
				for (let j = 0; j < batch.length; j++) {
					const resultat = blobs[j];
					if (resultat.status === 'rejected') {
						echecs.push({ id: batch[j].id, taille: batch[j].base64.length, erreur: resultat.reason });
						continue;
					}
					const blob = resultat.value;
					if (blob && blob.size > 100) {
						const objectUrl = URL.createObjectURL(blob);
						this.objectUrlCache.set(batch[j].id, objectUrl);
						result.set(batch[j].id, objectUrl);
					}
				}
				// Micro-pause entre batchs de conversion
				if (i + CONVERT_BATCH < validRecords.length) {
					await new Promise(resolve => setTimeout(resolve, 0));
				}
			}

			if (echecs.length > 0) {
				console.error('❌ Blobs impossibles à convertir:', echecs);
				enregistrerErreurTelemetrie(
					echecs[0].erreur,
					'photo.blob.conversion',
					{ 'photo.id': echecs[0].id, 'photo.base64_length': echecs[0].taille, 'photo.echecs': echecs.length }
				);
			}

			// 4. lastAccessedAt bulk update (fire-and-forget, hors chemin critique)
			const loadedIds = validRecords.map(r => r.id);
			if (loadedIds.length > 0) {
				photoDB.photo_blobs.bulkUpdate(
					loadedIds.map(id => ({ key: id, changes: { lastAccessedAt: new Date() } }))
				).catch(() => { /* ignore */ });
			}
		} catch (error) {
			console.error('❌ Erreur bulkGetObjectUrls:', error);
			enregistrerErreurTelemetrie(error, 'photo.blob.bulk_get', { 'photo.demandees': idsToLoad.length, 'photo.chargees': result.size });
		}

		return result;
	}

	/**
	 * Récupère directement le base64 depuis IndexedDB (pour upload/sync)
	 * Plus efficace que getBlob() + reconversion pour les uploads
	 */
	async getBase64(photoId: string): Promise<string | undefined> {
		try {
			const record = await photoDB.photo_blobs.get(photoId);
			
			if (record) {
				// Mettre à jour lastAccessedAt en fire-and-forget (non bloquant)
				photoDB.photo_blobs.update(photoId, {
					lastAccessedAt: new Date()
				}).catch(() => { /* ignore */ });
				
				return record.base64;
			}
			
			return undefined;
		} catch (error) {
			console.error('❌ Erreur lors de la récupération du base64:', error);
			return undefined;
		}
	}

	/**
	 * Supprime tous les blobs associés à un rapport
	 * C'est la méthode clé pour le nettoyage !
	 */
	async deleteBlobsByRapportId(rapportId: number): Promise<number> {
		const startTime = performance.now();
		
		try {
			// 1. Récupérer tous les blobs du rapport
			const blobRecords = await photoDB.photo_blobs
				.where('rapportId')
				.equals(rapportId)
				.toArray();
			
			// 2. Récupérer toutes les photos du rapport (pour révoquer objectUrls)
			const photos = await db.photo
				.where('rapport.id')
				.equals(rapportId)
				.toArray();
			
			if (blobRecords.length === 0 && photos.length === 0) {
				console.log('🧹 Aucune photo à nettoyer pour le rapport:', rapportId);
				return 0;
			}
			
			let totalSize = 0;
			let revokedCount = 0;
			
			// 3. Révoquer tous les objectUrls en mémoire
			for (const photo of photos) {
				// Révoquer depuis le cache du service
				this.revokeObjectUrl(photo.id);
				if (photo.objectUrl) {
					try {
						URL.revokeObjectURL(photo.objectUrl);
						revokedCount++;
					} catch (e) {
						console.warn('⚠️ Impossible de révoquer objectUrl pour photo:', photo.id, e);
					}
				}
			}
			
			// 4. Calculer la taille totale des blobs (depuis base64)
			for (const blobRecord of blobRecords) {
				// Estimer la taille depuis base64 (taille base64 * 0.75 ≈ taille blob)
				const base64Length = blobRecord.base64.split(',')[1]?.length || 0;
				totalSize += Math.floor(base64Length * 0.75);
			}
			
			// 5. Supprimer les blobs de la table photo_blobs
			await photoDB.photo_blobs
				.where('rapportId')
				.equals(rapportId)
				.delete();
			
			// 6. Supprimer les métadonnées des photos de la base
			const deletedCount = await db.photo
				.where('rapport.id')
				.equals(rapportId)
				.delete();
			
			const duration = performance.now() - startTime;
			
			console.log(`🧹 Nettoyage rapport ${rapportId}:`, {
				photos: deletedCount,
				blobs_deleted: blobRecords.length,
				objectUrls_revoked: revokedCount,
				size: `${(totalSize / 1024 / 1024).toFixed(2)} MB`,
				duration: `${duration.toFixed(0)}ms`
			});
			
			return totalSize;
			
		} catch (error) {
			console.error('❌ Erreur lors du nettoyage des blobs du rapport:', rapportId, error);
			throw error;
		}
	}
	
	/**
	 * Supprime un blob spécifique
	 */
	async deleteBlob(photoId: string): Promise<void> {
		try {
			// 1. Récupérer la photo pour révoquer l'objectUrl
			const photo = await db.photo.get(photoId as any);
			
			if (photo?.objectUrl) {
				URL.revokeObjectURL(photo.objectUrl);
			}
			
			// 2. Supprimer le blob
			await photoDB.photo_blobs.delete(photoId);
			
			// 3. Supprimer les métadonnées
			await db.photo.delete(photoId as any);
			
			console.log('🧹 Photo supprimée:', photoId);
		} catch (error) {
			console.error('❌ Erreur lors de la suppression du blob:', photoId, error);
			throw error;
		}
	}
	
	/**
	 * Nettoyage automatique des blobs orphelins ou anciens
	 * (optionnel - peut être appelé au démarrage de l'app)
	 */
	async cleanupOldBlobs(maxAgeDays: number = 30): Promise<number> {
		const cutoffDate = new Date();
		cutoffDate.setDate(cutoffDate.getDate() - maxAgeDays);
		
		try {
			// 1. Récupérer tous les BIs existants
			const allBis = await db.bi.toArray();
			const existingRapportIds = new Set(allBis.map(bi => bi.rapport?.id).filter(Boolean));
			
			// 2. Récupérer tous les blobs
			const allBlobRecords = await photoDB.photo_blobs.toArray();
			
			let totalCleaned = 0;
			let orphanedCount = 0;
			let validatedCount = 0;
			
			for (const blobRecord of allBlobRecords) {
				const rapportId = blobRecord.rapportId;
				
				// Blob orphelin (rapport n'existe plus)
				if (!existingRapportIds.has(rapportId)) {
					await photoDB.photo_blobs.delete(blobRecord.photoId);
					
					// Supprimer aussi les métadonnées photo si elles existent
					const photo = await db.photo.get(blobRecord.photoId as any);
					if (photo) {
						if (photo.objectUrl) {
							URL.revokeObjectURL(photo.objectUrl);
						}
						await db.photo.delete(photo.id as any);
					}
					
					// Calculer taille depuis base64
					const base64Length = blobRecord.base64.split(',')[1]?.length || 0;
					totalCleaned += Math.floor(base64Length * 0.75);
					orphanedCount++;
					continue;
				}
				
				// Blob de rapport validé (optionnel - peut être nettoyé)
				if (existingRapportIds.has(rapportId)) {
					const bi = allBis.find(b => b.rapport?.id === rapportId);
					
					// Si le rapport est validé par le BE (dateCloseBe), on peut nettoyer
					if (bi?.rapport?.dateCloseBe) {
						await photoDB.photo_blobs.delete(blobRecord.photoId);
						
						const photo = await db.photo.get(blobRecord.photoId as any);
						if (photo) {
							if (photo.objectUrl) {
								URL.revokeObjectURL(photo.objectUrl);
							}
							await db.photo.delete(photo.id as any);
						}
						
						// Calculer taille depuis base64
						const base64Length = blobRecord.base64.split(',')[1]?.length || 0;
						totalCleaned += Math.floor(base64Length * 0.75);
						validatedCount++;
					}
				}
			}
			
			console.log(`🧹 Purge automatique:`, {
				orphaned: orphanedCount,
				validated: validatedCount,
				total_cleaned: `${(totalCleaned / 1024 / 1024).toFixed(2)} MB`
			});
			
			return totalCleaned;
			
		} catch (error) {
			console.error('❌ Erreur lors de la purge automatique:', error);
			throw error;
		}
	}
	
	/**
	 * Obtient les statistiques de stockage des photos
	 */
	async getStorageStats(): Promise<{
		photoCount: number;
		blobCount: number;
		totalSize: number;
		byRapport: Map<number, { count: number; size: number }>;
	}> {
		try {
			const allPhotos = await db.photo.toArray();
			const allBlobRecords = await photoDB.photo_blobs.toArray();
			
			let totalSize = 0;
			const byRapport = new Map<number, { count: number; size: number }>();
			
			// Calculer à partir des base64 (source de vérité pour la taille)
			for (const blobRecord of allBlobRecords) {
				// Calculer taille depuis base64
				const base64Length = blobRecord.base64.split(',')[1]?.length || 0;
				const size = Math.floor(base64Length * 0.75);
				totalSize += size;
				
				// Grouper par rapport
				const rapportId = blobRecord.rapportId;
				if (rapportId) {
					const stats = byRapport.get(rapportId) || { count: 0, size: 0 };
					stats.count++;
					stats.size += size;
					byRapport.set(rapportId, stats);
				}
			}
			
			return {
				photoCount: allPhotos.length,
				blobCount: allBlobRecords.length,
				totalSize,
				byRapport
			};
			
		} catch (error) {
			console.error('❌ Erreur lors du calcul des statistiques:', error);
			return {
				photoCount: 0,
				blobCount: 0,
				totalSize: 0,
				byRapport: new Map()
			};
		}
	}
	
	/**
	 * Stocke directement une chaîne Base64 dans IndexedDB
	 * Évite la conversion coûteuse Blob -> Base64 quand on a déjà la source
	 */
	async storeBase64(photoId: string, rapportId: number, base64: string): Promise<void> {
		try {
			const record: PhotoBlobRecord = {
				photoId,
				rapportId,
				base64,
				createdAt: new Date(),
				lastAccessedAt: new Date()
			};
			
			await photoDB.photo_blobs.put(record);
			
			// Estimation taille (x0.75 pour ratio base64/binaire)
			const sizeKB = (base64.length * 0.75 / 1024).toFixed(2);
			console.log(`📦 Blob stocké (Direct Base64) pour photo: ${photoId} | ~${sizeKB} KB`);
		} catch (error) {
			console.error('❌ Erreur lors du stockage du base64:', error);
			throw error;
		}
	}
}